import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from '@/lib/motion-wrapper';
import { PlayCircle, ChevronLeft, ChevronRight } from 'lucide-react';

const testimonials = [
  { id: 1, videoId: 'A9MZ9kXirYA' },
  { id: 2, videoId: 'rMuiM5H4WHM' },
  { id: 3, videoId: 'SHxHYa_COOY' },
  { id: 4, videoId: 'fhg5fOSREr4' },
  { id: 5, videoId: '3NpBmVgWRrs' },
  { id: 6, videoId: 'xw3p7oNMqTw' },
  { id: 7, videoId: 'FijCMibk8LU' },
];

const VideoTestimonial = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [mobileIndex, setMobileIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState<number | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const itemsPerPage = 4;
  const totalPages = Math.ceil(testimonials.length / itemsPerPage);
  const currentPage = Math.floor(currentIndex / itemsPerPage);

  const nextPage = () => {
    setCurrentIndex(prev => {
      const nextIndex = prev + itemsPerPage;
      return nextIndex >= testimonials.length ? 0 : nextIndex;
    });
  };

  const prevPage = () => {
    setCurrentIndex(prev => {
      const prevIndex = prev - itemsPerPage;
      return prevIndex < 0 ? Math.max(0, testimonials.length - itemsPerPage) : prevIndex;
    });
  };

  // Auto-advance to next video every 10 seconds if not hovered (optimized for performance)
  useEffect(() => {
    if (isHovered) return;
    
    const timer = setInterval(() => {
      setMobileIndex(prev => (prev + 1) % testimonials.length);
      setIsPlaying(null);
    }, 10000); // Increased from 5s to 10s for better performance

    return () => clearInterval(timer);
  }, [isHovered, mobileIndex]);

  const visibleTestimonials = [];
  for (let i = 0; i < itemsPerPage; i++) {
    const index = (currentIndex + i) % testimonials.length;
    visibleTestimonials.push(testimonials[index]);
  }

  return (
    <section className="w-full py-16 md:py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="container px-4 md:px-6">
        <div className="flex flex-col items-center justify-center space-y-8 text-center">
          <div className="space-y-4">
            <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl">
              What Our Customers Say
            </h2>
            <p className="mx-auto max-w-[700px] text-gray-500 md:text-xl/relaxed lg:text-base/relaxed xl:text-xl/relaxed dark:text-gray-400">
              Don't just take our word for it. Hear from people who've experienced the NaturalPuff difference.
            </p>
          </div>
          
          <div 
            className="w-full max-w-6xl mx-auto relative group"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            ref={containerRef}
          >
            {/* Desktop View - 4 testimonials */}
            <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-6 px-4">
              {visibleTestimonials.map((testimonial, idx) => (
                <div key={testimonial.id} className="relative group/item">
                  <div className="relative overflow-hidden rounded-2xl shadow-2xl" style={{ aspectRatio: '9/16' }}>
                    {isPlaying === testimonial.id ? (
                      <div className="w-full h-full">
                        <iframe
                          width="100%"
                          height="100%"
                          src={`https://www.youtube.com/embed/${testimonial.videoId}?autoplay=1&mute=1&rel=0`}
                          title={`Customer Testimonial ${testimonial.id} - NaturalPuff`}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          className="w-full h-full"
                        />
                      </div>
                    ) : (
                      <div 
                        className="relative w-full h-full cursor-pointer"
                        onClick={() => setIsPlaying(testimonial.id)}
                      >
                        <img
                          src={`https://img.youtube.com/vi/${testimonial.videoId}/maxresdefault.jpg`}
                          alt={`Customer testimonial ${testimonial.id}`}
                          className="w-full h-full object-cover transition-transform duration-150 group-hover/item:opacity-90"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = `https://img.youtube.com/vi/${testimonial.videoId}/hqdefault.jpg`;
                          }}
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-12 h-12 md:w-14 md:h-14 bg-white/90 rounded-full flex items-center justify-center shadow-lg transition-transform duration-150 group-hover/item:scale-105">
                            <PlayCircle className="w-8 h-8 md:w-10 md:h-10 text-primary" />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile View - Single testimonial */}
            <div className="md:hidden w-full max-w-md mx-auto relative">
              <div className="relative overflow-hidden rounded-2xl shadow-2xl" style={{ aspectRatio: '9/16' }}>
                {isPlaying !== null ? (
                  <div className="w-full h-full">
                    <iframe
                      width="100%"
                      height="100%"
                      src={`https://www.youtube.com/embed/${testimonials[mobileIndex].videoId}?autoplay=1&mute=1&rel=0`}
                      title={`Customer Testimonial - NaturalPuff`}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                    />
                  </div>
                ) : (
                  <div 
                    className="relative w-full h-full cursor-pointer"
                    onClick={() => setIsPlaying(mobileIndex)}
                  >
                    <img
                      src={`https://img.youtube.com/vi/${testimonials[mobileIndex].videoId}/maxresdefault.jpg`}
                      alt="Customer testimonial"
                      className="w-full h-full object-cover transition-transform duration-150"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = `https://img.youtube.com/vi/${testimonials[mobileIndex].videoId}/hqdefault.jpg`;
                      }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center shadow-lg transition-transform duration-150 group-hover:scale-105">
                        <PlayCircle className="w-10 h-10 text-primary" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Mobile Navigation Arrows */}
              <button 
                onClick={(e) => { 
                  e.stopPropagation(); 
                  setMobileIndex(prev => (prev - 1 + testimonials.length) % testimonials.length);
                  setIsPlaying(null);
                }}
                className="md:hidden absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center shadow-lg z-10"
                aria-label="Previous testimonial"
              >
                <ChevronLeft className="w-6 h-6 text-gray-800" />
              </button>
              
              <button 
                onClick={(e) => { 
                  e.stopPropagation(); 
                  setMobileIndex(prev => (prev + 1) % testimonials.length);
                  setIsPlaying(null);
                }}
                className="md:hidden absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center shadow-lg z-10"
                aria-label="Next testimonial"
              >
                <ChevronRight className="w-6 h-6 text-gray-800" />
              </button>
              
              {/* Mobile Dots Indicator */}
              <div className="flex justify-center mt-4 gap-2 md:hidden">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={(e) => {
                      e.stopPropagation();
                      setMobileIndex(index);
                      setIsPlaying(null);
                    }}
                    className={`w-2 h-2 rounded-full transition-transform duration-150 ${
                      index === mobileIndex ? 'bg-primary w-4' : 'bg-gray-300'
                    }`}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))}
              </div>
            </div>

            {/* Navigation Arrows - Desktop */}
            <div className="hidden md:block">
              <button 
                onClick={(e) => { e.stopPropagation(); prevPage(); }}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-12 w-10 h-10 md:w-12 md:h-12 bg-white/90 rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-transform duration-150 z-10"
                aria-label="Previous page"
              >
                <ChevronLeft className="w-6 h-6 text-gray-800" />
              </button>
              
              <button 
                onClick={(e) => { e.stopPropagation(); nextPage(); }}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-12 w-10 h-10 md:w-12 md:h-12 bg-white/90 rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-transform duration-150 z-10"
                aria-label="Next page"
              >
                <ChevronRight className="w-6 h-6 text-gray-800" />
              </button>
            </div>

            {/* Page indicator */}
            <div className="hidden md:flex justify-center mt-6 gap-2">
              {Array.from({ length: totalPages }).map((_, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentIndex(index * itemsPerPage);
                  }}
                  className={`w-3 h-3 rounded-full transition-transform duration-150 ${
                    index === currentPage ? 'bg-primary w-8' : 'bg-gray-300'
                  }`}
                  aria-label={`Go to page ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VideoTestimonial;
